import { useRef, useState } from "react";
import { toast } from "sonner";
import { Notepad } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Amber banner shown on any job with needs_docs=true & !docs_completed_at.
 * Lets office/worker add photos + notes to complete documentation and
 * clear it from the "Needs Docs" queue. Shared by worker JobDetail and
 * admin AdminJobDetail.
 */
export default function NeedsDocsBanner({ job, onDone }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("kind", "after");
    fd.append("file", file);
    await api.post(`/jobs/${job.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" }});
  };

  const submit = async () => {
    setBusy(true);
    try {
      const files = Array.from(fileRef.current?.files || []);
      let uploadFails = 0;
      for (const f of files) {
        try { await uploadOne(f); } catch (e) { uploadFails += 1; }
      }
      // If the user attached files and they ALL failed, don't clear docs — surface the error.
      if (files.length > 0 && uploadFails === files.length) {
        toast.error("All uploads failed — docs not marked complete");
        setBusy(false);
        return;
      }
      await api.post(`/jobs/${job.id}/complete-docs`, { notes: notes || "" });
      if (uploadFails > 0) {
        toast.warning(`Docs marked complete (${uploadFails} photo${uploadFails === 1 ? "" : "s"} failed to upload)`);
      } else {
        toast.success("Docs completed — cleared from queue");
      }
      setOpen(false);
      setNotes("");
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not complete docs");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 p-3 border-2 border-amber-500 bg-amber-50" data-testid="needs-docs-banner">
      <div className="flex items-start gap-2">
        <Notepad weight="fill" size={20} className="text-amber-600 flex-shrink-0 mt-0.5"/>
        <div className="flex-1 min-w-0">
          <div className="font-black uppercase tracking-widest text-xs text-amber-800">Documentation still needed</div>
          <div className="text-sm text-slate-700 mt-0.5">
            {job.quick_closed_by_name ? `${job.quick_closed_by_name} quick-${job.quick_close_status || "closed"} this job. ` : ""}
            Add photos + notes when you have downtime.
          </div>
          {job.quick_close_reason && (
            <div className="mt-1 text-xs text-slate-600 italic">"{job.quick_close_reason}"</div>
          )}
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="complete-docs-btn"
            className="h-9 px-3 bg-amber-600 hover:bg-amber-700 text-white font-black uppercase text-[11px] tracking-widest whitespace-nowrap">
            Add Docs
          </button>
        )}
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes about what happened / what was done…"
            data-testid="docs-notes-input"
            className="w-full p-2 border-2 border-[#0f172a] text-sm resize-none"/>
          <div>
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple
              data-testid="docs-photos-input"
              className="text-xs"/>
          </div>
          <div className="flex gap-2">
            <button onClick={submit} disabled={busy} data-testid="docs-submit-btn"
              className="flex-1 h-10 bg-amber-600 hover:bg-amber-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-50">
              {busy ? "Saving…" : "Mark Docs Complete"}
            </button>
            <button onClick={() => setOpen(false)} data-testid="docs-cancel-btn"
              className="h-10 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
